import { useRef, useEffect, useMemo } from "react";
import { ChatAskMessage } from "@/zapi-frontend/stores/chatAskStore";
import { Bot, User } from "lucide-react";
import { Avatar, AvatarFallback } from "@libs/zapi-ui/shadcn";
import ReactMarkdown from "react-markdown";
import { MagnifyingGlass } from "@phosphor-icons/react";

type Props = {
  messages: ChatAskMessage[];
  streamingContent: string;
  isStreaming: boolean;
  statusMessage: string | null;
};

function MessageBubble({ message }: { message: ChatAskMessage }) {
  const isUser = message.role === "user";

  return (
    <div className={`flex items-start gap-2 max-w-full ${isUser ? "flex-row-reverse" : ""}`}>
      <Avatar className="h-8 w-8">
        <AvatarFallback>{isUser ? <User size={16} /> : <Bot size={16} />}</AvatarFallback>
      </Avatar>
      <div
        className={`rounded-2xl shadow-sm px-4 py-3 max-w-[75%] ${
          isUser ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white" : "bg-gray-50 border border-gray-100"
        }`}
      >
        <div className={`prose prose-sm max-w-none ${isUser ? "text-white prose-invert" : "text-gray-800"}`}>
          {isUser ? (
            <p className="mb-0">{message.content}</p>
          ) : (
            <ReactMarkdown
              components={{
                a: ({ ...props }) => (
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a {...props} target="_blank" rel="noopener noreferrer" className="underline text-blue-500" />
                ),
                p: ({ ...props }) => <p {...props} className="mb-3 last:mb-0" />,
                ul: ({ ...props }) => <ul {...props} className="list-disc pl-5 mb-3" />,
                ol: ({ ...props }) => <ol {...props} className="list-decimal pl-5 mb-3" />,
                li: ({ ...props }) => <li {...props} className="mb-1" />
              }}
            >
              {message.content}
            </ReactMarkdown>
          )}
        </div>
      </div>
    </div>
  );
}

function StreamingBubble({ content, statusMessage }: { content: string; statusMessage: string | null }) {
  return (
    <div className="flex items-start gap-2 max-w-full">
      <Avatar className="h-8 w-8">
        <AvatarFallback>
          <Bot size={16} />
        </AvatarFallback>
      </Avatar>
      <div className="rounded-2xl shadow-sm px-4 py-3 max-w-[75%] bg-gray-50 border border-gray-100">
        {content ? (
          <div className="prose prose-sm max-w-none text-gray-800">
            <ReactMarkdown
              components={{
                p: ({ ...props }) => <p {...props} className="mb-3 last:mb-0" />,
                ul: ({ ...props }) => <ul {...props} className="list-disc pl-5 mb-3" />,
                ol: ({ ...props }) => <ol {...props} className="list-decimal pl-5 mb-3" />,
                li: ({ ...props }) => <li {...props} className="mb-1" />
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        ) : null}
        {statusMessage && (
          <div className={`flex items-center gap-2 ${content ? "mt-2 pt-2 border-t border-gray-100" : ""}`}>
            <MagnifyingGlass size={14} className="text-blue-500 animate-pulse" weight="bold" />
            <span className="text-xs text-blue-600">{statusMessage}</span>
          </div>
        )}
        {!content && !statusMessage && (
          <div className="flex space-x-1 items-center p-1">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        )}
      </div>
    </div>
  );
}

export function ChatAskMessageList({ messages, streamingContent, isStreaming, statusMessage }: Props) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const messagesHash = useMemo(() => messages.map((m) => m.id).join("-"), [messages]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timeout);
  }, [messagesHash, streamingContent, isStreaming, statusMessage]);

  return (
    <div className="h-full overflow-y-auto px-4 py-4">
      <div className="space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex items-start ${message.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <MessageBubble message={message} />
          </div>
        ))}
        {isStreaming && (
          <div className="flex items-start justify-start">
            <StreamingBubble content={streamingContent} statusMessage={statusMessage} />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}
